/**
 * M4S-5777 X67 — the written statistics standard a study type's crosstabs follow.
 *
 * A standard is the significance a crosstab starts on (the tests, the two confidence levels, what
 * is compared, the minimum base a column needs for letters, any correction for many comparisons)
 * plus the sentences Research writes about it (how weights enter, how significance on an opt-in
 * panel is described). It lives in a study type's setup (`StudySetup.statistics`, X8), so an admin
 * edits it as configuration; the two below are the code copies the setup falls back to.
 *
 * A crosstab records which standard it started from (`StandardRef`, inside
 * `ai_layout.state.studyType`), with the values that standard set, so every table and file can say
 * which standard it follows, or that the statistics were changed from it ("Custom"), without
 * reading the setup again. `describeStandard` is the one sentence every surface prints.
 *
 * Decision 2 (17 Oct): outside iOOH, the industry default. Pairwise tests within each banner
 * group, 95% uppercase and 90% lowercase, no letters under a base of 30, no correction.
 *
 * Pure: no React, no storage, no network. Nothing here imports the workspace.
 */

import { SIG_CORRECTIONS, type SigCorrection } from './sig-correction';

/** What a crosstab's significance compares (`CrosstabSettings['sigScope']`). */
export type StandardScope = 'group' | 'all' | 'total';
/** Which test the engine runs (`CrosstabSettings['statTest']`). */
export type StandardTest = 'auto' | 'z' | 'chi2';

export interface StatsStandard {
    /** Stable id, lower-case words and hyphens, e.g. `industry-default`. */
    id: string;
    /** Bumped by every admin save of the setup that holds it. */
    version: number;
    /** What the product prints, e.g. "Industry default". */
    label: string;
    /** The first (uppercase) confidence level, a percent. */
    confidence: number;
    /** The second (lowercase) level, a percent below the first, or null for none. */
    confidence2: number | null;
    sigScope: StandardScope;
    statTest: StandardTest;
    sigCorrection: SigCorrection;
    /** Fewer answerers than this and a column gets no letters (`suppressionThreshold`). */
    minSigBase: number;
    /** Below this base a cell is called out as a low base (`lowBaseWarn`). */
    lowBaseWarn: number;
    /** Research's sentence on the tests. */
    tests: string;
    /** Research's sentence on how weights enter the test. */
    weighting: string;
    /** Research's sentence on describing significance on an opt-in panel. */
    optInPanel: string;
}

const COMMON_TEXT = {
    tests: 'Two-proportion z-tests between each pair of columns, run on each column’s answerers.',
    weighting: 'When a crosstab is weighted, each column is tested on its effective base (Kish).',
    optInPanel:
        'Respondents come from an opt-in panel, so significance describes differences beyond chance within this sample, not a margin of error for the population.',
} as const;

/**
 * Decision 2: the industry default, for every study type without a standard of its own. The
 * figures match what the engine already does by default (within each banner group, the overlap
 * correction for shared respondents, letters only on bases of 30 or more), plus the second level.
 */
export const INDUSTRY_DEFAULT_STANDARD: Readonly<StatsStandard> = Object.freeze({
    id: 'industry-default',
    version: 1,
    label: 'Industry default',
    confidence: 95,
    confidence2: 90,
    sigScope: 'group',
    statTest: 'auto',
    sigCorrection: 'none',
    minSigBase: 30,
    lowBaseWarn: 100,
    ...COMMON_TEXT,
});

/**
 * iOOH Brand Lift: 95% uppercase with 90% lowercase within each banner group (Allyson Wehn and
 * Jonathan Pruitt, 2026-10-06; docs/pdlc/crosstabs-starting-setup.md §4). `IOOH_CONFIDENCE` in
 * sig-confidence.ts is read off this, so the two can never disagree.
 */
export const IOOH_STANDARD: Readonly<StatsStandard> = Object.freeze({
    id: 'iooh',
    version: 1,
    label: 'iOOH Brand Lift',
    confidence: 95,
    confidence2: 90,
    sigScope: 'group',
    statTest: 'auto',
    sigCorrection: 'none',
    minSigBase: 30,
    lowBaseWarn: 100,
    ...COMMON_TEXT,
    tests: 'Two-proportion z-tests between each pair of columns within each banner group (Exposed against Unexposed, and the demographic splits), run on each column’s answerers.',
});

/** The first confidence levels the rail offers. A standard may name only these. */
export const STANDARD_CONFIDENCES: readonly number[] = [90, 95, 99];
/** The second (lowercase) levels the rail offers, each only below the first. */
export const STANDARD_SECOND_CONFIDENCES: readonly number[] = [90, 95];

const SCOPES: readonly StandardScope[] = ['group', 'all', 'total'];
const TESTS: readonly StandardTest[] = ['auto', 'z', 'chi2'];
const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const isObject = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

const text = (v: unknown, max: number): string | null =>
    typeof v === 'string' && v.trim() && v.trim().length <= max ? v.trim() : null;

const wholeAtLeast = (v: unknown, min: number, max: number): number | null =>
    typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max ? v : null;

/**
 * A standard from untrusted JSON (a setup row or an admin's PUT body), or the reasons it is not
 * one. Strict: an admin save that names a level the rail cannot show, or a second level not below
 * the first, is refused rather than quietly repaired.
 */
export function validateStatsStandard(
    raw: unknown,
): { ok: true; standard: StatsStandard } | { ok: false; errors: string[] } {
    if (!isObject(raw)) return { ok: false, errors: ['The statistics standard is missing.'] };
    const errors: string[] = [];
    const id = typeof raw.id === 'string' && ID_PATTERN.test(raw.id) ? raw.id : null;
    if (!id) errors.push('The standard needs an id of lower-case words and hyphens.');
    const version = wholeAtLeast(raw.version, 1, 1_000_000);
    if (version === null) errors.push('The standard’s version must be a whole number from 1.');
    const label = text(raw.label, 80);
    if (!label) errors.push('The standard needs a name of up to 80 characters.');
    const confidence = STANDARD_CONFIDENCES.includes(raw.confidence as number)
        ? (raw.confidence as number)
        : null;
    if (confidence === null) {
        errors.push(`The confidence level must be one of ${STANDARD_CONFIDENCES.join(', ')}.`);
    }
    let confidence2: number | null = null;
    if (raw.confidence2 !== null && raw.confidence2 !== undefined) {
        if (!STANDARD_SECOND_CONFIDENCES.includes(raw.confidence2 as number)) {
            errors.push('The second confidence level must be one of the rail’s levels, or none.');
        } else if (confidence !== null && (raw.confidence2 as number) >= confidence) {
            errors.push('The second confidence level must be below the first.');
        } else confidence2 = raw.confidence2 as number;
    }
    const sigScope = SCOPES.includes(raw.sigScope as StandardScope)
        ? (raw.sigScope as StandardScope)
        : null;
    if (!sigScope) errors.push('What is compared must be group, all or total.');
    const statTest = TESTS.includes(raw.statTest as StandardTest)
        ? (raw.statTest as StandardTest)
        : null;
    if (!statTest) errors.push('The test must be auto, z or chi2.');
    const sigCorrection = (SIG_CORRECTIONS as readonly unknown[]).includes(raw.sigCorrection)
        ? (raw.sigCorrection as SigCorrection)
        : null;
    if (!sigCorrection) errors.push(`The correction must be one of ${SIG_CORRECTIONS.join(', ')}.`);
    const minSigBase = wholeAtLeast(raw.minSigBase, 0, 10_000);
    if (minSigBase === null) errors.push('The minimum base must be a whole number.');
    const lowBaseWarn = wholeAtLeast(raw.lowBaseWarn, 0, 100_000);
    if (lowBaseWarn === null) errors.push('The low-base warning must be a whole number.');
    const tests = text(raw.tests, 600);
    const weighting = text(raw.weighting, 600);
    const optInPanel = text(raw.optInPanel, 600);
    if (!tests || !weighting || !optInPanel) {
        errors.push(
            'The standard needs its three written sentences (tests, weighting, opt-in panel).',
        );
    }
    if (errors.length) return { ok: false, errors };
    return {
        ok: true,
        standard: {
            id: id!,
            version: version!,
            label: label!,
            confidence: confidence!,
            confidence2,
            sigScope: sigScope!,
            statTest: statTest!,
            sigCorrection: sigCorrection!,
            minSigBase: minSigBase!,
            lowBaseWarn: lowBaseWarn!,
            tests: tests!,
            weighting: weighting!,
            optInPanel: optInPanel!,
        },
    };
}

/** The workspace statistics fields a standard sets on a new crosstab. */
export interface StandardFields {
    confidence: number;
    confidence2: number | null;
    sigScope: StandardScope;
    statTest: StandardTest;
    sigCorrection: SigCorrection;
    suppressionThreshold: number;
    lowBaseWarn: number;
}

/** The settings a standard puts on a new crosstab, by their workspace names. */
export function standardFields(standard: StatsStandard): StandardFields {
    return {
        confidence: standard.confidence,
        confidence2: standard.confidence2,
        sigScope: standard.sigScope,
        statTest: standard.statTest,
        sigCorrection: standard.sigCorrection,
        suppressionThreshold: standard.minSigBase,
        lowBaseWarn: standard.lowBaseWarn,
    };
}

/**
 * What a crosstab records about the standard it started from: which one, which version, its name,
 * and the values it set. The values are kept so "Custom" is worked out from the record alone, even
 * after an admin edits the setup.
 */
export interface StandardRef {
    id: string;
    version: number;
    label: string;
    fields: StandardFields;
}

export function standardRefOf(standard: StatsStandard): StandardRef {
    return {
        id: standard.id,
        version: standard.version,
        label: standard.label,
        fields: standardFields(standard),
    };
}

const FIELD_KEYS: readonly (keyof StandardFields)[] = [
    'confidence',
    'confidence2',
    'sigScope',
    'statTest',
    'sigCorrection',
    'suppressionThreshold',
    'lowBaseWarn',
];

/** A recorded reference in its one canonical shape, or null. Values out of shape read as none. */
export function parseStandardRef(raw: unknown): StandardRef | null {
    if (!isObject(raw) || !isObject(raw.fields)) return null;
    const id = typeof raw.id === 'string' && ID_PATTERN.test(raw.id) ? raw.id : null;
    const version = wholeAtLeast(raw.version, 1, 1_000_000);
    const label = text(raw.label, 80);
    if (!id || version === null || !label) return null;
    const f = raw.fields;
    const confidence = typeof f.confidence === 'number' && f.confidence > 0 ? f.confidence : null;
    const confidence2 =
        typeof f.confidence2 === 'number' && f.confidence2 > 0 ? f.confidence2 : null;
    const sigScope = SCOPES.includes(f.sigScope as StandardScope)
        ? (f.sigScope as StandardScope)
        : null;
    const statTest = TESTS.includes(f.statTest as StandardTest)
        ? (f.statTest as StandardTest)
        : null;
    const sigCorrection = (SIG_CORRECTIONS as readonly unknown[]).includes(f.sigCorrection)
        ? (f.sigCorrection as SigCorrection)
        : null;
    const suppressionThreshold = wholeAtLeast(f.suppressionThreshold, 0, 10_000);
    const lowBaseWarn = wholeAtLeast(f.lowBaseWarn, 0, 100_000);
    if (
        confidence === null ||
        !sigScope ||
        !statTest ||
        !sigCorrection ||
        suppressionThreshold === null ||
        lowBaseWarn === null
    ) {
        return null;
    }
    return {
        id,
        version,
        label,
        fields: {
            confidence,
            confidence2,
            sigScope,
            statTest,
            sigCorrection,
            suppressionThreshold,
            lowBaseWarn,
        },
    };
}

/** The names a changed field is called by in "Custom (…)". */
const FIELD_NAMES: Record<keyof StandardFields, string> = {
    confidence: 'confidence levels',
    confidence2: 'confidence levels',
    sigScope: 'what is compared',
    statTest: 'the test',
    sigCorrection: 'the correction for many comparisons',
    suppressionThreshold: 'the minimum base',
    lowBaseWarn: 'the low-base warning',
};

/** Which parts of the statistics differ from the standard's, in plain words, de-duplicated. */
export function changedFromStandard(
    ref: StandardRef,
    current: Partial<Record<keyof StandardFields, unknown>>,
): string[] {
    const names = FIELD_KEYS.filter((k) => {
        const now = current[k] ?? (k === 'confidence2' ? null : undefined);
        return now !== undefined && now !== ref.fields[k];
    }).map((k) => FIELD_NAMES[k]);
    return [...new Set(names)];
}

/**
 * The one sentence every table and file prints about the standard (X67):
 *  - "Statistics standard: Industry default (version 1)."
 *  - "Statistics standard: Custom, based on Industry default (version 1); changed: the test."
 *  - with no reference (a crosstab made before standards were recorded): it says so.
 */
export function describeStandard(
    ref: StandardRef | null,
    current: Partial<Record<keyof StandardFields, unknown>>,
): string {
    if (!ref) {
        return 'Statistics standard: none recorded (this crosstab was made before standards were recorded).';
    }
    const changed = changedFromStandard(ref, current);
    const name = `${ref.label} (version ${ref.version})`;
    return changed.length
        ? `Statistics standard: Custom, based on ${name}; changed: ${changed.join(', ')}.`
        : `Statistics standard: ${name}.`;
}

/**
 * The short form under each table on screen: "Standard: Industry default v1", or "Standard: Custom,
 * based on Industry default v1". Null for a crosstab with no recorded standard, so an older
 * crosstab's tables carry no line (its files still say none was recorded).
 */
export function describeStandardShort(
    ref: StandardRef | null,
    current: Partial<Record<keyof StandardFields, unknown>>,
): string | null {
    if (!ref) return null;
    const name = `${ref.label} v${ref.version}`;
    return changedFromStandard(ref, current).length
        ? `Standard: Custom, based on ${name}`
        : `Standard: ${name}`;
}
